<template>
  <div>
    <div class="form-group p-t-10">
      <div class="col-md-4 control-label">Select your business entity type *</div>
      <div class="col-md-6 col-xs-8">
        <div class="radio">
          <label>
            <input
              v-model="hasCaBusinessEntity"
              :value="1"
              type="radio"
              name="has_ca_business_entity"
            />
            <i class="input-helper" />
            I have a business entity setup
          </label>
        </div>
        <div class="radio">
          <label>
            <input
              v-model="hasCaBusinessEntity"
              :value="0"
              type="radio"
              name="has_ca_business_entity"
            />
            <i class="input-helper" />
            I do not have a business entity setup
          </label>
        </div>
      </div>
    </div>

    <div v-show="hasCaBusinessEntity">
      <hr />
      <div class="form-group p-t-30">
        <label class="col-md-4 control-label" for="caBusinessEntityNameInput">
          Business entity name *
        </label>
        <div class="col-md-6 col-xs-8">
          <input
            id="caBusinessEntityNameInput"
            v-model="caBusinessEntityName"
            name="ca_business_entity_name"
            placeholder="Business entity name"
            type="text"
            required="required"
            class="form-control"
          />
        </div>
      </div>

      <div class="form-group">
        <label for="ca_business_number" class="col-md-4 control-label">
          {{ isQuebec ? 'Business number or NEQ' : 'Business number' }}
          <CompanyNumberTooltip :market="isQuebec ? 'CA_Q' : 'CA'" />
        </label>
        <div class="col-md-6">
          <input
            id="ca_business_number"
            v-model="caBusinessNumber"
            class="form-control"
            :placeholder="
              isQuebec
                ? 'Your NEQ if you have one, otherwise your business number'
                : 'Business number e.g. 123456789RT1234'
            "
            name="ca_business_number"
            type="text"
          />
        </div>
      </div>

      <div class="form-group p-t-10">
        <div class="col-md-4 control-label">Are you registered for GST/HST?</div>
        <div class="col-md-6 col-xs-8">
          <div class="radio">
            <label>
              <input
                v-model="isGstHstRegistered"
                :value="1"
                type="radio"
                name="is_gst_hst_registered"
              />
              <i class="input-helper" />
              Yes, I have a GST/HST registration
            </label>
          </div>
          <div class="radio">
            <label>
              <input
                v-model="isGstHstRegistered"
                :value="0"
                type="radio"
                name="is_gst_hst_registered"
              />
              <i class="input-helper" />
              No, I will be billing without GST/HST
            </label>
          </div>
        </div>
      </div>

      <div v-show="isGstHstRegistered === 1" class="form-group">
        <label for="gst_hst_number" class="col-md-4 control-label">GST/HST number *</label>
        <div class="col-md-6">
          <input
            id="gst_hst_number"
            v-model="gstHstNumber"
            class="form-control"
            placeholder="GST/HST number"
            name="vat_no"
            type="text"
            required
          />
        </div>
      </div>
    </div>

    <div v-show="!hasCaBusinessEntity">
      <hr />
      <div class="form-group">
        <label for="ca_social_insurance_number" class="col-md-4 control-label">
          Social insurance number (SIN)
        </label>
        <div class="col-md-6">
          <input
            id="ca_social_insurance_number"
            v-model="caSocialInsuranceNumber"
            class="form-control"
            placeholder="Social insurance number e.g. 123456789"
            name="ca_social_insurance_number"
            type="text"
          />
        </div>
      </div>
    </div>

    <div class="form-group">
      <div class="col-sm-6 col-sm-offset-4 col-xs-12">
        <SubmitButton :disabled="!canSave" :text="$t('Save')" auto-submit />
      </div>
    </div>
  </div>
</template>

<script>
import CompanyNumberTooltip from '@/components/compliances/BusinessEntity/CompanyNumberTooltip.vue'
import { booleanToIntNullable, intToBooleanNullable } from '@/utils/forms/ValueNormalisers'
import { deepClone } from '@/utils/tools'
import SubmitButton from '@/components/SubmitButton.vue'

export default {
  name: 'FreelancerCaBusinessEntityForm',
  components: {
    SubmitButton,
    CompanyNumberTooltip,
  },
  props: {
    businessEntity: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
    currentSelectedLocation: {
      type: Object,
      required: false,
    },
  },
  data() {
    const internalBusinessEntity = deepClone(this.businessEntity)
    const internalComplianceData = deepClone(this.complianceData)
    return {
      internalBusinessEntity,
      internalComplianceData,
    }
  },
  computed: {
    isQuebec() {
      return this.currentSelectedLocation?.state === 'QC'
    },
    caBusinessEntityName: {
      get() {
        return this.internalBusinessEntity.companyName
      },
      set(caBusinessEntityName) {
        this.internalBusinessEntity.companyName = caBusinessEntityName
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    caBusinessNumber: {
      get() {
        return this.internalBusinessEntity.companyNo
      },
      set(caBusinessNumber) {
        this.internalBusinessEntity.companyNo = caBusinessNumber
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    caSocialInsuranceNumber: {
      get() {
        return this.hasCaBusinessEntity === 0
          ? this.internalBusinessEntity.nationalIdentifier
          : null
      },
      set(caSocialInsuranceNumber) {
        if (this.hasCaBusinessEntity === 0) {
          this.internalBusinessEntity.nationalIdentifier = caSocialInsuranceNumber
          this.$emit('update:businessEntity', this.internalBusinessEntity)
        }
      },
    },
    canSave() {
      if (this.hasCaBusinessEntity === null) {
        return false
      }
      if (this.hasCaBusinessEntity === 0) {
        return true
      }
      // For business entity, require business name
      if (!this.caBusinessEntityName || this.caBusinessEntityName === '') {
        return false
      }
      // If GST/HST registered, require GST number
      if (this.isGstHstRegistered === 1 && (!this.gstHstNumber || this.gstHstNumber === '')) {
        return false
      }
      return true
    },
    hasCaBusinessEntity: {
      get() {
        return booleanToIntNullable(
          this.internalComplianceData.complianceInput?.data?.has_ca_business_entity ?? null,
        )
      },
      set(hasCaBusinessEntity) {
        if (this.internalComplianceData.complianceInput === null) {
          this.internalComplianceData.complianceInput = { data: {} }
        }
        this.internalComplianceData.complianceInput.data.has_ca_business_entity =
          intToBooleanNullable(hasCaBusinessEntity)

        if (hasCaBusinessEntity === 0) {
          this.caBusinessEntityName = null
          this.caBusinessNumber = null
          this.internalBusinessEntity.taxNo = null
          this.internalBusinessEntity.data.gst_hst_registered = null
        } else if (hasCaBusinessEntity === 1) {
          this.internalBusinessEntity.nationalIdentifier = null
        }

        this.$emit('update:complianceData', this.internalComplianceData)
      },
    },
    isGstHstRegistered: {
      get() {
        return booleanToIntNullable(this.internalBusinessEntity.data.gst_hst_registered ?? null)
      },
      set(isGstHstRegistered) {
        this.internalBusinessEntity.data.gst_hst_registered =
          intToBooleanNullable(isGstHstRegistered)
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    gstHstNumber: {
      get() {
        return this.hasCaBusinessEntity === 1 ? this.internalBusinessEntity.taxNo : null
      },
      set(gstHstNumber) {
        if (this.hasCaBusinessEntity === 1) {
          this.internalBusinessEntity.taxNo = gstHstNumber
          this.$emit('update:businessEntity', this.internalBusinessEntity)
        }
      },
    },
  },
  watch: {
    currentSelectedLocation(val, oldVal) {
      if (oldVal?.state !== val?.state) {
        this.internalBusinessEntity.companyNo = ''
      }
    },
  },
}
</script>
